import React, { useState, useEffect, useCallback } from 'react'
import Image from '@/components/image'
import Styles from './style.module.scss'

interface SwiperGalleryProps {
  images: string[]
  interval?: number // 轮播间隔时间（毫秒），0或不传则不轮播
  className?: string
}

export default function SwiperGallery({ images, interval = 0, className = '' }: SwiperGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [animationKey, setAnimationKey] = useState(0)

  // 自动轮播逻辑
  useEffect(() => {
    if (interval <= 0 || images.length <= 1) return

    const timer = setInterval(() => {
      // 只有在没有悬停且没有过渡时才自动轮播
      if (!isHovered && !isTransitioning) {
        setCurrentIndex(prev => (prev + 1) % images.length)
      }
    }, interval)

    return () => clearInterval(timer)
  }, [interval, images.length, isHovered, isTransitioning])

  // 切换到指定索引
  const goToSlide = useCallback((index: number) => {
    if (index === currentIndex || isTransitioning) return
    
    setIsTransitioning(true)
    setAnimationKey(prev => prev + 1) // 强制重新渲染以触发动画
    setCurrentIndex(index)
    
    // 重置过渡状态，与CSS动画时长保持一致
    setTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [currentIndex, isTransitioning])

  // 计算显示的图片索引
  const getImageIndex = (position: 'prev' | 'current' | 'next') => {
    const total = images.length
    if (total === 0) return 0
    
    switch (position) {
      case 'prev':
        return (currentIndex - 1 + total) % total
      case 'current':
        return currentIndex
      case 'next':
        return (currentIndex + 1) % total
      default:
        return 0
    }
  }

  if (!images || images.length === 0) {
    return <div className={`${Styles.swiperGallery} ${className}`}>暂无图片</div>
  }

  return (
    <div className={`${Styles.swiperGallery} ${className}`}>
      {/* 图片容器 */}
      <div 
        key={animationKey}
        className={`${Styles.imageContainer} ${isTransitioning ? Styles.transitioning : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* 左侧图片（尾图） */}
        <div 
          className={`${Styles.imageWrapper} ${Styles.prevImage}`}
          onClick={() => goToSlide(getImageIndex('prev'))}
        >
          <Image 
            src={images[getImageIndex('prev')]} 
            alt={`Image ${getImageIndex('prev') + 1}`}
            className={Styles.image}
          />
        </div>

        {/* 中央图片（主图） */}
        <div className={`${Styles.imageWrapper} ${Styles.currentImage}`}>
          <Image 
            src={images[getImageIndex('current')]} 
            alt={`Image ${getImageIndex('current') + 1}`}
            className={Styles.image}
          />
        </div>

        {/* 右侧图片（第二张图） */}
        <div 
          className={`${Styles.imageWrapper} ${Styles.nextImage}`}
          onClick={() => goToSlide(getImageIndex('next'))}
        >
          <Image 
            src={images[getImageIndex('next')]} 
            alt={`Image ${getImageIndex('next') + 1}`}
            className={Styles.image}
          />
        </div>
      </div>

      {/* 底部指示器 */}
      <div className={Styles.indicatorContainer}>
        {images.map((_, index) => (
          <div
            key={index}
            className={`${Styles.indicator} ${index === currentIndex ? Styles.active : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  )
}
